import React, { useEffect, useState } from 'react';
import { css, keyframes } from '@emotion/css';
import { useStartAddressChangeProcess } from '@/apiHooks/addressChange.hook';
import { useGetSession } from '@/apiHooks/session.hook';
import { report } from '@/tracking/tracking';
import { TrackingEvents } from '@/tracking/trackingEvents';
import { useHasMeinPlus } from '@/customHooks/useHasMeinPlus.hook';

const skeletonLoading = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const styles = {
  loader: css`
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: ${skeletonLoading} 2s infinite;
  `,
  flexRow: css`
    flex-direction: row;
  `,
  flexCenter: css`
    display: flex;
    align-items: center;
    justify-content: center;
  `,
  header: css`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 20px;
  `,
  title: css`
    width: 200px;
    height: 24px;
    margin-bottom: 12px;
    border-radius: 4px;
    justify-content: center;
  `,
  counter: css`
    width: 30px;
    height: 24px;
    border-radius: 12px;
  `,
  gridList: css`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 20px;
  `,
  card: css`
    height: 100px;
    border-radius: 8px;
    width: 350px;
  `,
  container: css`
    position: relative;
    width: 100%;
    height: 100vh;
    display: flex;
    flex-direction: column;
  `,
  iframeContainer: css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    transition: opacity 0.5s ease-in-out;
  `,
  visible: css`
    opacity: 1;
  `,
};
const LoadingSkeleton = () => {
  return (
    <div data-testid='loading-skeleton' className={`${styles.title} ${styles.container}`} style={{ padding: '40px' }}>
      <div className={styles.header}>
        <div className={`${styles.title} ${styles.loader}`} style={{ width: '300px' }}></div>
        <div className={`${styles.title} ${styles.loader}`}></div>
      </div>
      <div className={`${styles.flexCenter}`}>
        <div className={`${styles.card} ${styles.loader}`} style={{ marginRight: '14px' }}></div>
        <div className={`${styles.card} ${styles.loader}`}></div>
      </div>
    </div>
  );
};

const FinoPage = () => {
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const { initializing: isSessionLoading } = useGetSession();
  const isMeinPlus = useHasMeinPlus();
  const {
    mutate: initiateAddressChange,
    isLoading: isAddressChangeInitiating,
    data: addressChangeData,
  } = useStartAddressChangeProcess();

  const handleIframeLoad = () => {
    setIframeLoaded(true);
  };

  useEffect(() => {
    if (!isSessionLoading) {
      initiateAddressChange();
    }
  }, [isSessionLoading]);

  useEffect(() => {
    report(TrackingEvents.ADDRESS_CHANGE.IFRAME, isMeinPlus);
    document.body.classList.add('fino-page-active');
    return () => {
      document.body.classList.remove('fino-page-active');
    };
  }, []);

  const isLoading = isAddressChangeInitiating || !iframeLoaded;

  // TODO: post-cleanup  return to error page and adjust copy on error page for IssueType.ADDRESS_CHANGE_SERVICE_PAGE_ERROR
  // return <IssuePage type={IssueType.ADDRESS_CHANGE_SERVICE_PAGE_ERROR} />;

  return (
    <div className={`${styles.container} fino-page`}>
      {isLoading && <LoadingSkeleton data-testid='loading-skeleton' />}
      {!isAddressChangeInitiating && !!(addressChangeData as any)?.iframeUrl && (
        <div className={`${styles.iframeContainer} ${!isLoading ? styles.visible : ''}`}>
          <iframe
            src={(addressChangeData as any)?.iframeUrl}
            title='fino'
            width='100%'
            height='100%'
            style={{ border: 0 }}
            onLoad={handleIframeLoad}
            sandbox='allow-scripts allow-same-origin allow-modals allow-popups allow-forms allow-popups-to-escape-sandbox allow-top-navigation'
          />
        </div>
      )}
    </div>
  );
};

export default FinoPage;
